import { useCallback, useLayoutEffect, useRef, useState } from 'react';

const RESIZE_DEBOUNCE_MS = 100;

const getWindowWidth = (): number => (typeof window === 'undefined' ? 0 : window.innerWidth);

/**
 * Re-evaluates `query` against the window width on (debounced) resize and returns its result.
 */
const useResponsive = (query: ({ width }: { width: number }) => boolean): boolean => {
  const [result, setResult] = useState(false);
  const resultRef = useRef(result);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

  const update = useCallback(() => {
    const nextResult = query({ width: getWindowWidth() });

    if (nextResult !== resultRef.current) {
      resultRef.current = nextResult;
      setResult(nextResult);
    }
  }, [query]);

  useLayoutEffect(() => {
    const handleResize = (): void => {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(update, RESIZE_DEBOUNCE_MS);
    };

    window.addEventListener('resize', handleResize);
    update();

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutRef.current);
    };
  }, [update]);

  return result;
};

export default useResponsive;
